import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useReserveContext } from '../../../contexts/useReserveContext';
import { fetchAvailableTimesByStaff } from '../../../utils/reservation/by-staff/fetchAvailableTimesByStaff';
import { fetchOpenDatesForStaffReserve } from '../../../utils/reservation/by-staff/fetchOpenDatesForStaffReserve';

import ReserveStepHeader from '../ReserveStepHeader';
import ReserveCalendarGrid from '../ReserveCalendarGrid';
import { useI18n } from '../../../i18n';

const pad2 = (n: number) => String(n).padStart(2, '0');
const toMonthKeyFromYMD = (ymd?: string | null) => (ymd ? ymd.slice(0, 7) : null);
const toMonthKeyFromDate = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;

const SelectDateByStaff: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const isEditing = searchParams.get('edit') === '1';
  const {
    storeId,
    selectedStaffId,
    selectedDate,
    selectedStartTime,
    setSelectedDate,
    setSelectedStartTime,
    selectedServices,
  } = useReserveContext();

  const [openDates, setOpenDates] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const totalMinutes = useMemo(
    () => selectedServices.reduce((sum, service) => sum + (Number(service.duration_minutes) || 0), 0),
    [selectedServices],
  );
  const serviceIds = useMemo(
    () =>
      selectedServices
        .map((service) => Number(service.serviceId))
        .filter((serviceId) => Number.isFinite(serviceId) && serviceId > 0),
    [selectedServices],
  );

  const [month, setMonth] = useState<string>(() => {
    const m = toMonthKeyFromYMD(selectedDate);
    return m ?? toMonthKeyFromDate(new Date());
  });

  useEffect(() => {
    if (!storeId) {
      toast.info(t('date.selectStoreFirst'));
      navigate('/reserve/by-staff/store');
      return;
    }
    if (!selectedStaffId) {
      toast.info(t('date.selectStaffFirst'));
      navigate('/reserve/by-staff/staff');
      return;
    }
    if (isEditing && !selectedStartTime) {
      toast.info(t('date.returnAfterTime'));
      navigate('/reserve/by-staff/confirm-reservation');
      return;
    }
    if (selectedServices.length === 0) {
      toast.info(t('date.selectServiceFirst'));
      navigate(isEditing ? '/reserve/by-staff/confirm-reservation' : '/reserve/by-staff/service');
      return;
    }

    const load = async () => {
      try {
        setLoading(true);
        const dates = await fetchOpenDatesForStaffReserve(storeId, selectedStaffId, 90);
        if (isEditing) {
          const availableDates = await Promise.all(
            dates.map(async (date) => {
              const slots = await fetchAvailableTimesByStaff({
                storeId,
                staffId: selectedStaffId,
                date,
                durationMinutes: totalMinutes,
                serviceIds,
              });
              return slots.some((slot) => slot.time === selectedStartTime && slot.available)
                ? date
                : null;
            }),
          );
          setOpenDates(availableDates.filter((date): date is string => Boolean(date)));
        } else {
          setOpenDates(dates);
        }

        const m = toMonthKeyFromYMD(selectedDate);
        if (m) setMonth(m);
      } catch (e: any) {
        console.error(e);
        toast.error(e?.message || t('date.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [
    storeId,
    selectedStaffId,
    selectedDate,
    selectedStartTime,
    selectedServices.length,
    totalMinutes,
    serviceIds,
    isEditing,
    navigate,
    t,
  ]);

  const openDateSet = useMemo(() => new Set(openDates), [openDates]);

  const { minMonth, maxMonth } = useMemo(() => {
    if (!openDates.length) {
      return { minMonth: undefined as string | undefined, maxMonth: undefined as string | undefined };
    }
    const sorted = [...openDates].sort();
    return {
      minMonth: sorted[0].slice(0, 7),
      maxMonth: sorted[sorted.length - 1].slice(0, 7),
    };
  }, [openDates]);

  const handleSelectDate = (ymd: string) => {
    if (!openDateSet.has(ymd)) return;

    setSelectedDate(ymd);
    if (isEditing) {
      navigate('/reserve/by-staff/confirm-reservation');
      return;
    }

    setSelectedStartTime(null);
    navigate('/reserve/by-staff/time');
  };

  return (
    <div className="flex flex-col min-h-[70vh] space-y-4">
      <ReserveStepHeader
        title={t('date.chooseTitle')}
        onBack={() =>
          navigate(isEditing ? '/reserve/by-staff/confirm-reservation' : '/reserve/by-staff/service')
        }
        step={4}
        totalSteps={5}
      />

      {loading ? (
        <p className="text-center text-slate-400 text-sm">{t('date.loadingCalendar')}</p>
      ) : openDates.length === 0 ? (
        <div className="rounded-[28px] border border-rose-200 bg-rose-50 p-4 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-rose-100 p-2 text-rose-600">
              <AlertTriangle className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-rose-700">{t('date.cannotChange')}</div>
              <div className="mt-1 text-sm leading-6 text-rose-600">
                {t('date.noAlternativeStaff')}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <ReserveCalendarGrid
          month={month}
          onMonthChange={setMonth}
          selectedDate={selectedDate}
          openDateSet={openDateSet}
          onSelectDate={handleSelectDate}
          minMonth={minMonth}
          maxMonth={maxMonth}
          weekStartsOn={1}
        />
      )}
    </div>
  );
};

export default SelectDateByStaff;
